import IconCatch from '@/components/icons/icon-catch/icon-catch';
import { TypeOfCancer, TypeOfCancerIcon } from '@/components/icons/icon-catch/types';
import { ROUTE_SINGLE_CANCER_VIEW } from '@/constants/routes';
import useIsMobile from '@/hooks/use-is-mobile';
import { formatCancerTypeNameWithCapitalization, getRoute } from '@/utils';
import { Box, Flex, IconArrowRight, Link, Text } from '@cmpsr/components';
import { Link as ReactRouterLink } from 'react-router-dom';
import RiskImpactProgressBar from './components/risk-impact-progress-bar';
import getBadgeAndProgressBarVariant from './get-badge-variant.util';

type RiskImpactGraphProps = {
  cancerType: TypeOfCancer;
  impact: number;
  average: number;
  isCancerPageLink: boolean;
};

function RiskImpactGraphMobile({ cancerType, impact, average, isCancerPageLink }: RiskImpactGraphProps) {
  const { TextBadgeComponent, progressBarVariant } = getBadgeAndProgressBarVariant(impact, average);
  const formattedCancerType = formatCancerTypeNameWithCapitalization(cancerType);

  return (
    <>
      <Flex w="full" alignItems="flex-start" justifyContent="space-between" mb="spacer-5">
        <Flex flex="1" gap="spacer-6">
          <IconCatch
            backgroundColor="background-page"
            icon={TypeOfCancerIcon[cancerType.toLowerCase() as TypeOfCancer]}
            alt="cancer type icon"
            size="m"
            borderRadius="radii-2"
          />
          <Flex direction="column" gap="spacer-1" mb="spacer-5">
            <Text variant="text-body-display-XS" textAlign="start">
              {formattedCancerType}
            </Text>
            <TextBadgeComponent />
          </Flex>
        </Flex>
        {isCancerPageLink && (
          <Link
            variant="primary-alt"
            to={getRoute(ROUTE_SINGLE_CANCER_VIEW, { cancerId: cancerType })}
            as={ReactRouterLink}
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxSize="36px"
          >
            <IconArrowRight size="s" />
          </Link>
        )}
      </Flex>
      <RiskImpactProgressBar impact={impact} average={average} progressBarVariant={progressBarVariant} />
    </>
  );
}

function RiskImpactGraphDesktop({ cancerType, impact, average, isCancerPageLink }: RiskImpactGraphProps) {
  const { TextBadgeComponent, progressBarVariant } = getBadgeAndProgressBarVariant(impact, average);
  const formattedCancerType = formatCancerTypeNameWithCapitalization(cancerType);

  return (
    <Flex w="full" alignItems="flex-start" justifyContent="space-between" gap="spacer-6">
      <IconCatch
        backgroundColor="background-page"
        icon={TypeOfCancerIcon[cancerType.toLowerCase() as TypeOfCancer]}
        alt="cancer type icon"
        size="xl"
        borderRadius="radii-2"
      />
      <Flex flexDir="column" flex="1" gap="spacer-3">
        <Flex justifyContent="space-between" alignItems="center" gap="spacer-4">
          <Flex gap="spacer-4" alignItems="center">
            <Text variant="text-body-display-S" textAlign="start">
              {formattedCancerType}
            </Text>
            <TextBadgeComponent />
          </Flex>
          {isCancerPageLink && (
            <Link
              variant="primary-alt"
              to={getRoute(ROUTE_SINGLE_CANCER_VIEW, { cancerId: cancerType })}
              as={ReactRouterLink}
              trailingIcon={<IconArrowRight size="s" />}
              height="fit-content"
              whiteSpace="nowrap"
            >
              Learn more
            </Link>
          )}
        </Flex>
        <Box mt="spacer-3">
          <RiskImpactProgressBar impact={impact} average={average} progressBarVariant={progressBarVariant} />
        </Box>
      </Flex>
    </Flex>
  );
}

export default function RiskImpactGraph({ impact, average, ...props }: RiskImpactGraphProps) {
  const isMobile = useIsMobile();

  return isMobile ? (
    <RiskImpactGraphMobile impact={impact} average={average} {...props} />
  ) : (
    <RiskImpactGraphDesktop impact={impact} average={average} {...props} />
  );
}
